import type { Operation } from 'fast-json-patch';

import { assert, assertNonNullable } from '@disy/lib/custom-error';

import { OL_DEFAULT_MAP_SRS } from 'cadenza/acquisition/acquisition-layer-utils';
import { ajax } from 'cadenza/api-client/api';
import {
  HEADER_EXPORT_TYPE,
  HEADER_POLL_REQUEST,
  MIMETYPE_DISY_WORKORDER_ID,
  PARAMETER_MAP_RESOLUTION,
  PARAMETER_SRS_CODE,
} from 'cadenza/api-client/api-constants';
import type { AttributeValueRowDto } from 'cadenza/api-client/dto/attribute-value-row-dto';
import type { ChangesRequestDto } from 'cadenza/api-client/dto/changes-request-dto';
import type { ChartViewDataDto } from 'cadenza/api-client/dto/chart-view-data-dto';
import type {
  ControllerViewOptionDto,
  ControllerViewVerifiedOptionDto,
} from 'cadenza/api-client/dto/controller-view-data-dto';
import type { DataViewAttributeValueDto } from 'cadenza/api-client/dto/data-view-attribute-value-dto';
import type { DataViewAttributeValuesDto } from 'cadenza/api-client/dto/data-view-attribute-values-dto';
import type { DeletionDeadlineInfoDto } from 'cadenza/api-client/dto/deletion-deadline-dto';
import type { DimensionDto } from 'cadenza/api-client/dto/dimension-dto';
import type { EnrichmentDependencyDto } from 'cadenza/api-client/dto/enrichment-dependency-dto';
import type { IndicatorViewDto } from 'cadenza/api-client/dto/indicator-view-dto';
import type { ObjectInfoAutoResolvedAttributesDto } from 'cadenza/api-client/dto/object-info-auto-resolved-attributes-dto';
import type { ObjecttypesDataDependenciesDto } from 'cadenza/api-client/dto/objecttype-data-dependencies-dto';
import type { ObjecttypeDetailsDto } from 'cadenza/api-client/dto/objecttype-details-dto';
import type { UploadedFileDto } from 'cadenza/api-client/dto/uploaded-file-dto';
import { toSearchParams } from 'cadenza/api-client/dto/uploaded-file-dto';
import type { WorkbookMetadataDto } from 'cadenza/api-client/dto/workbook-metadata-dto';
import type { WorkbookWithVersionDto } from 'cadenza/api-client/dto/workbook-with-version-dto';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import type { Authority } from 'cadenza/api-client/srs-api';
import type { TableData } from 'cadenza/api-client/tables-api';
import { tableParams } from 'cadenza/api-client/tables-api';
import type { SaveWorkbookResult } from 'cadenza/api-client/workbook-api-util';
import { Command, isCommandType } from 'cadenza/api-client/workbook-commands';
import type { WorkOrderId } from 'cadenza/api-client/workorder-api';
import type { TableDataDto } from 'cadenza/base-table/model/table-data-dto';
import type {
  DefaultLegendItemEntryDto,
  IsochroneLegendItemEntryDto,
  LegendItemDto,
  RouteLegendItemEntryDto,
} from 'cadenza/map/dto/legend-item-dto';
import type { AddAttributeToViewPredictedResult } from 'cadenza/model/add-attribute-to-view-predicted-result';
import { type ImageMediaType, MIMETYPE_JSON, MIMETYPE_TEXT, MIMETYPE_ZIP } from 'cadenza/model/media-type';
import type { DataProtectionContextId } from 'cadenza/repository/model/data-protection-context-id.gen';
import type { MediaItemDto } from 'cadenza/repository/model/media-item-dto';
import type { MediaItemPropertyName } from 'cadenza/repository/model/media-item-property-name.gen';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { ProjectId } from 'cadenza/repository/model/project-id.gen';
import type { ProjectName } from 'cadenza/repository/model/project-name.gen';
import type { PublicationStage } from 'cadenza/repository/model/publication-stage.gen';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';
import { type WorkbookNameAndHash, workbookNameAndHashString } from 'cadenza/repository/model/workbook-name-and-hash';
import type { WorkbookName } from 'cadenza/repository/model/workbook-name.gen';
import type { ResultTableAjaxRequestParams } from 'cadenza/result-table/result-table';
import type { TableConfigurationDto } from 'cadenza/result-table/table-configuration-dto';
import type { ImportObjecttypeEventDto } from 'cadenza/selfservice/import-data/api/import-objecttype-event-dto';
import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';
import { download } from 'cadenza/utils/download';
import { getLocation } from 'cadenza/utils/location';
import { settingsJsonParser } from 'cadenza/utils/settings-json-parser';
import type { ExportActionContext } from 'cadenza/workbook/actions/export-actions';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookVersion } from 'cadenza/workbook/api/workbook-version';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { WorkbookSpatialConditionValueItemDto } from 'cadenza/workbook/model/condition-dto';
import type { ConditionId } from 'cadenza/workbook/model/condition-id.gen';
import type { DataBrowserDto } from 'cadenza/workbook/model/data-browser-dto';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type { ImportWorkbookDto } from 'cadenza/workbook/model/import-workbook-dto';
import type { ObjectInfoElementsDto } from 'cadenza/workbook/model/object-info-elements-dto';
import type { VisualizationCategory } from 'cadenza/workbook/model/visualization-category.gen';
import type { VisualizationDto } from 'cadenza/workbook/model/visualization-dto';
import type { WorkbookHash } from 'cadenza/workbook/model/workbook-hash';
import type { Settings } from 'cadenza/workbook/workbook-settings-util';
import type { WorkbookJobScheduleConfiguration } from 'cadenza/workbook/worksheet-subscription/worksheet-subscription-wizard/workbook-job-schedule-configuration';

/**
 * Prevent the given workbook from being removed from the workbook graph.
 *
 * @param workbookNameAndHash - Name and hash of the workbook to keep alive
 * @return A promise for whether the requests was successful
 */
export function keepWorkbookAlive(workbookNameAndHash: WorkbookNameAndHash) {
  return ajax(createWorkbookUrl(workbookNameAndHash), {
    method: 'HEAD',
    headers: { [HEADER_POLL_REQUEST]: 'poll' }, // do not keep the session alive, and set a dummy value in the header since empty headers are illegal
  });
}

/**
 * Returns visualizations.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param dataViewId - Id of the data view
 * @return Promise containing visualizations
 */
export function getVisualizations(workbookNameAndHash: WorkbookNameAndHash, dataViewId?: DataViewId) {
  return ajax<Record<VisualizationCategory, VisualizationDto[]>>(
    createWorkbookUrl(workbookNameAndHash, { dataViewId }) + '/visualizations',
    { dataType: 'json' },
  );
}

export function exportMultipleDataViewData(options: {
  authority?: Authority;
  baseFileName?: string;
  dataViewId: DataViewId[];
  mediaType: string;
  uploadedFile?: UploadedFileDto;
  workbookNameAndHash: WorkbookNameAndHash;
}) {
  const { authority, baseFileName, dataViewId, mediaType, uploadedFile, workbookNameAndHash } = options;
  let url = `/workbooks/${workbookNameAndHashString(workbookNameAndHash)}/data-views;dataViewIds=`;

  // concatenate comma seperated dataViewIds to url
  for (let i = 0; i < dataViewId.length; i++) {
    url += dataViewId[i];
    if (i === dataViewId.length - 1) {
      url += '/export-work-orders';
    } else {
      url += ',';
    }
  }

  const searchParams = uploadedFile ? toSearchParams(uploadedFile) : new URLSearchParams();
  if (authority) {
    searchParams.append('authority', authority);
  }
  if (baseFileName) {
    searchParams.append('baseFileName', baseFileName);
  }
  if (searchParams.toString()) {
    url += `?${searchParams}`;
  }

  return ajax<WorkOrderId>(url, {
    method: 'POST',
    headers: { [HEADER_EXPORT_TYPE]: mediaType, Accept: MIMETYPE_DISY_WORKORDER_ID },
  });
}

export function checkIfDataViewsDataCanBeExported(
  workbookNameAndHash: WorkbookNameAndHash,
  dataViewIds: DataViewId[],
  mediaType: string,
  actionContext?: ExportActionContext,
) {
  let url =
    createWorkbookUrl(workbookNameAndHash) + `/data-views;dataViewIds=${dataViewIds.join(',')}/can-export-work-orders`;

  const searchParams = new URLSearchParams();
  if (actionContext?.type === 'SELECTION_CART') {
    searchParams.append('useSelection', String(true));
  }
  if (searchParams.toString()) {
    url += `?${searchParams}`;
  }

  return ajax<WorkOrderId>(url, {
    method: 'OPTIONS',
    headers: { [HEADER_EXPORT_TYPE]: mediaType },
  });
}

export function exportDataViewData(options: {
  authority?: Authority;
  dataViewId: DataViewId;
  mediaType: string;
  actionContext?: ExportActionContext;
  uploadedFile?: UploadedFileDto;
  workbookNameAndHash: WorkbookNameAndHash;
}) {
  const { authority, dataViewId, mediaType, actionContext, uploadedFile, workbookNameAndHash } = options;
  let url = createWorkbookUrl(workbookNameAndHash, { dataViewId });

  const searchParams = uploadedFile ? toSearchParams(uploadedFile) : new URLSearchParams();
  if (authority) {
    searchParams.append('authority', authority);
  }
  if (actionContext?.type === 'SELECTION_CART') {
    searchParams.append('useSelection', String(true));
  }
  if (searchParams.toString()) {
    url += `?${searchParams}`;
  }

  return ajax<WorkOrderId>(url, { headers: { [HEADER_EXPORT_TYPE]: mediaType, Accept: MIMETYPE_DISY_WORKORDER_ID } });
}

export function visiblePerPrecondition(workbookNameAndHash: WorkbookNameAndHash, dataViewId: DataViewId) {
  const url = `/workbooks/${workbookNameAndHashString(workbookNameAndHash)}/data-views/${dataViewId}/visible-per-precondition`;
  return ajax<boolean>(url);
}

/**
 * Returns table data for the view.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param viewId - Id of the view
 * @param parameters - Request parameters
 * @return Data of the table
 */
export function getTableViewData(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  parameters: Pick<ResultTableAjaxRequestParams, 'offset' | 'limit' | 'signal'>,
) {
  const { offset, limit, signal } = parameters;
  const params = { offset: String(offset), limit: String(limit) };
  const baseUrl = createWorkbookUrl(workbookNameAndHash, { viewId }) + '/tableData?';
  const url = baseUrl + new URLSearchParams(params);
  return ajax<TableDataDto>(url, { dataType: 'json', signal });
}

/**
 * Returns table data for the individual values table. Table data is based on current workbook selection.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param viewId - Id of the view
 * @param parameters - Request parameters
 * @return Data of the table
 */
export function getIndividualValuesTableData(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  { signal, ...params }: ResultTableAjaxRequestParams,
) {
  return ajax<TableDataDto>(
    createWorkbookUrl(workbookNameAndHash, { viewId }) + '/individualValuesTableData?' + tableParams(params),
    { dataType: 'json', signal },
  );
}

/**
 * Returns chart data for the view.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param viewId - Id of the view
 * @param signal - Signal for aborting the request
 * @return Data of the chart
 */
export function getChartViewData(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  signal: AbortSignal,
): Promise<ChartViewDataDto> {
  return ajax(createWorkbookUrl(workbookNameAndHash, { viewId }) + '/chartData', { dataType: 'json', signal });
}

/**
 * Returns data for the controller view.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param viewId - Id of the view
 * @param searchString The string to search for
 * @param signal - Signal for aborting the request
 * @return Data of the controller
 */
export function getControllerViewOptions(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  searchString: string,
  signal: AbortSignal,
) {
  const baseUrl = createWorkbookUrl(workbookNameAndHash, { viewId }) + '/controller-options?';
  const url = baseUrl + new URLSearchParams({ searchString });
  return ajax<ControllerViewOptionDto[]>(url, { dataType: 'json', signal });
}

/**
 * Returns data for the controller view.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param viewId - Id of the view
 * @param signal - Signal for aborting the request
 * @return Data of the controller
 */
export function verifyControllerViewOption(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  changeToIndex: number | undefined,
  signal: AbortSignal,
) {
  const baseUrl = createWorkbookUrl(workbookNameAndHash, { viewId }) + '/verify-controller?';
  const url =
    changeToIndex !== undefined ? baseUrl + new URLSearchParams({ changeToIndex: String(changeToIndex) }) : baseUrl;
  return ajax<ControllerViewVerifiedOptionDto>(url, { dataType: 'json', signal });
}

/**
 * Returns table configuration for the view.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param viewId - Id of the view
 * @param signal - Signal for aborting the request
 * @return Setting of the table
 */
export function getTableViewConfiguration(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  signal: AbortSignal,
) {
  return ajax<TableConfigurationDto>(createWorkbookUrl(workbookNameAndHash, { viewId }) + '/tableConfiguration', {
    dataType: 'json',
    signal,
  });
}

/**
 * Returns table configuration for the individual values table.
 * Table configuration is based on current workbook selection.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param viewId - Id of the view
 * @param signal - Signal for aborting the request
 * @return Setting of the table
 */
export function getIndividualValuesTableConfiguration(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  signal: AbortSignal,
) {
  return ajax<TableConfigurationDto>(
    createWorkbookUrl(workbookNameAndHash, { viewId }) + '/individualValuesTableConfiguration',
    { dataType: 'json', signal },
  );
}

/**
 * Checks if currently visible drillThrough result can be downloaded with specified mimeType.
 * Check takes into account export limits configured in the system.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param viewId - Id of the view
 * @param mediaType - MIME type of expected result
 * @return Export check result
 */
export function checkIfIndividualValuesTableCanBeExported(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  mediaType: string,
) {
  return ajax<WorkOrderId>(createWorkbookUrl(workbookNameAndHash, { viewId }) + '/individualValues', {
    method: 'OPTIONS',
    headers: { [HEADER_EXPORT_TYPE]: mediaType },
  });
}

export interface IndividualValuesTableDataExportOptions {
  orderByColumnName?: string;
  orderAsc?: boolean;
  targetFile?: UploadedFileDto;
}

/**
 * Starts download of currently visible drillThrough result. Result will be prepared accordingly to provided mimeType.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param viewId - Id of the view
 * @param mediaType - MIME type of expected result
 * @param parameters - Request parameters
 * @param [parameters.orderByColumnName] - Attribute of a column to sort
 * @param [parameters.orderAsc] - Whether to sort in ascending order
 * @param [parameters.targetFile] - Details of target file to append data to
 * @return Export result
 */
export function exportIndividualValuesTableData(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  mediaType: string,
  { orderByColumnName, orderAsc, targetFile }: IndividualValuesTableDataExportOptions = {},
) {
  const baseUrl = createWorkbookUrl(workbookNameAndHash, { viewId });
  let data = orderByColumnName ? { orderByColumnName, orderAsc } : {};
  if (targetFile) {
    data = Object.assign(data, targetFile);
  }
  const headers = { Accept: MIMETYPE_DISY_WORKORDER_ID, [HEADER_EXPORT_TYPE]: mediaType };
  return ajax<WorkOrderId>(`${baseUrl}/individualValues`, { data, headers });
}

/**
 * Returns table configuration for the objecttype preview table.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param objecttypeIdString - objecttypeId of the selected objecttype
 * @param signal - Signal for aborting the request
 * @return Setting of the table
 */
export function getObjecttypePreviewTableConfiguration(
  workbookNameAndHash: WorkbookNameAndHash,
  objecttypeIdString: ObjecttypeIdString,
  signal: AbortSignal,
) {
  return ajax<TableConfigurationDto>(
    `/workbooks/${workbookNameAndHashString(workbookNameAndHash)}/objecttypes/${btoa(objecttypeIdString)}/preview-table-configuration`,
    { dataType: 'json', signal },
  );
}

/**
 * Returns table data for the objecttype preview table.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param objecttypeIdString - objecttypeId of the selected objecttype
 * @param parameters - Request parameters
 * @return Data of the table
 */
export function getObjecttypePreviewTableData(
  workbookNameAndHash: WorkbookNameAndHash,
  objecttypeIdString: ObjecttypeIdString,
  { signal, ...params }: ResultTableAjaxRequestParams,
) {
  return ajax<TableDataDto>(
    `/workbooks/${workbookNameAndHashString(workbookNameAndHash)}/objecttypes/${btoa(objecttypeIdString)}/preview-table-data?${tableParams(params)}`,
    { dataType: 'json', signal },
  );
}

/**
 * Returns displayed value for the view.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param viewId - Id of the view
 * @param signal - Signal for aborting the request
 * @return Data of the indicator
 */
export function getIndicatorViewData(workbookNameAndHash: WorkbookNameAndHash, viewId: ViewId, signal: AbortSignal) {
  return ajax<IndicatorViewDto>(createWorkbookUrl(workbookNameAndHash, { viewId }) + '/indicatorData', {
    dataType: 'json',
    signal,
  });
}

/**
 * Returns a list of rows matching to active attributes used in the given text view.
 */
export function getTextViewData(workbookNameAndHash: WorkbookNameAndHash, viewId: ViewId, signal: AbortSignal) {
  return ajax<Record<ActiveAttributeId, DataViewAttributeValuesDto>>(
    createWorkbookUrl(workbookNameAndHash, { viewId }) + '/text-view-data',
    { dataType: 'json', signal },
  );
}

/**
 * Returns a value to active attribute used in the given webpage view.
 */
export function getWebpageViewData(workbookNameAndHash: WorkbookNameAndHash, viewId: ViewId, signal: AbortSignal) {
  return ajax<DataViewAttributeValueDto>(createWorkbookUrl(workbookNameAndHash, { viewId }) + '/webpage-view-data', {
    dataType: 'json',
    signal,
  });
}

interface CommandDto<P = object> {
  /** the type of the command as {string} */
  type: string;

  /** payload The data to send with the command */
  payload: P;
}

interface GetPartialWorkbookStateResult {
  workbookNameAndHash: WorkbookNameAndHash;
  operations: Operation[];
  attributeSettingsDefaults?: Record<`${AttributeModificationKey}.default`, unknown>;
}

export interface ExecuteCommandResult extends GetPartialWorkbookStateResult {
  invalidViewsOrDataViews: (ViewId | DataViewId)[];
  invalidLayerIdsByViewId: Record<ViewId, LayerId[]>;
  dataViewsAffectedBySelectionChange: DataViewId[];
  viewsToZoomToDataExtent: ViewId[];
}

export interface GetWorkbookStateResult extends ExecuteCommandResult {
  unavailableObjecttypeIds: ObjecttypeIdString[];
  version: WorkbookVersion;
  publicationStage?: PublicationStage;
}

function commandToDto(command: Command): CommandDto {
  // Omit ignoreInOnDemand from the DTO, because it is only used on the frontend.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ignoreInOnDemand, ...payloadForDto } = command.payload;
  const commandDto: CommandDto<{ settings?: Settings }> = {
    type: command.type,
    payload: payloadForDto,
  };
  if (commandDto.payload.settings) {
    commandDto.payload.settings = stringifySettingsValues(commandDto.payload.settings);
  } else if (isCommandType(command, Command.COMPOSITE_COMMAND)) {
    (commandDto.payload as { commands: CommandDto[] }).commands = command.payload.commands.map(commandToDto);
  }
  return commandDto;
}

// exported only for testing purposes
export { commandToDto as __commandToDto__ };

function stringifySettingsValues(settings: Settings) {
  const result: Settings = {};
  Object.entries(settings).forEach(([key, value]) => {
    if (!key.endsWith('.default')) {
      result[key] = value == null || typeof value === 'string' ? value : JSON.stringify(value);
    }
  });
  return result;
}

/**
 * Executes the given command.
 *
 * @param workbookNameAndHash - The state of the workbook to extend from
 * @param command - The command to execute
 * @return Object reflecting the workbook model sparsely filled with only the changes
 */
export function executeCommand(workbookNameAndHash: WorkbookNameAndHash, command: Command) {
  assert(command, 'missing command');
  assert(command.type, 'missing command type');
  assert(command.payload, 'missing command payload');
  return ajax<ExecuteCommandResult>(createWorkbookUrl(workbookNameAndHash) + '/commands', {
    method: 'POST',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(commandToDto(command)),
    converters: { 'text json': settingsJsonParser },
  });
}

/**
 * Get workbook state via hash.
 *
 * @param workbookNameAndHash - Name and hash of the workbook to revert state
 * @return workbook state for worksheets, conditions and hierarchies
 */
export function getWorkbookState(workbookNameAndHash: WorkbookNameAndHash) {
  return ajax<GetWorkbookStateResult>(createWorkbookUrl(workbookNameAndHash), {
    headers: { Accept: MIMETYPE_JSON },
    converters: { 'text json': settingsJsonParser },
  });
}

export function getPartialWorkbookState(
  workbookNameAndHash: WorkbookNameAndHash,
  changesRequestDto: ChangesRequestDto,
) {
  return ajax<GetPartialWorkbookStateResult>(createWorkbookUrl(workbookNameAndHash), {
    type: 'POST',
    data: JSON.stringify(changesRequestDto),
    contentType: MIMETYPE_JSON,
    converters: { 'text json': settingsJsonParser },
  });
}

export function getObjectTypesAndDataSourcesVisibleInWorkbooks(projectId?: ProjectId) {
  let url = '/objecttypes';
  if (projectId) {
    const { repositoryName, itemName } = projectId;
    const urlParams = new URLSearchParams({ repositoryName, projectName: itemName });
    url += `?${urlParams}`;
  }
  return ajax<DataBrowserDto>(url, {
    headers: { Accept: MIMETYPE_JSON },
    converters: { 'text json': settingsJsonParser },
  });
}

export function getObjecttypesDataDependencies(workbookNameAndHash: WorkbookNameAndHash) {
  return ajax<ObjecttypesDataDependenciesDto>(
    `${createWorkbookUrl(workbookNameAndHash)}/objecttypes-data-dependencies`,
    {
      type: 'GET',
      headers: { Accept: MIMETYPE_JSON },
    },
  );
}

export function getEnrichmentDependencies(workbookNameAndHash: WorkbookNameAndHash, objecttypeId: ObjecttypeIdString) {
  return ajax<EnrichmentDependencyDto[]>(
    `${createWorkbookUrl(workbookNameAndHash)}/objecttypes/${window.btoa(objecttypeId)}/enrichment-dependencies`,
    {
      type: 'GET',
      headers: { Accept: MIMETYPE_JSON },
    },
  );
}

/**
 * Determine objecttype details like is deletable or in a writableRepository
 *
 * @param objecttypeId - the id of the objecttype with name and repository
 * @return details - requested details
 */
export function getObjecttypeSelfServiceDetails(objecttypeId: ObjecttypeIdString) {
  return ajax<ObjecttypeDetailsDto>(`/selfservice/objecttypes/${window.btoa(objecttypeId)}`, {
    type: 'GET',
    headers: { Accept: MIMETYPE_JSON },
  }).catch((error) => {
    throw new Error(`An error occurred while trying to check whether objecttype is deletable: ${error}`);
  });
}

export function getMediaItems(
  workbookNameAndHash: WorkbookNameAndHash,
  mediaTypes: ImageMediaType[],
  properties: Partial<Record<MediaItemPropertyName, string>>,
) {
  const urlParams = new URLSearchParams({ mediaTypes: mediaTypes.join(), ...properties });
  return ajax<MediaItemDto[]>(`${createWorkbookUrl(workbookNameAndHash)}/media-items?${urlParams}`, {
    type: 'GET',
    headers: { Accept: MIMETYPE_JSON },
  }).catch((error) => {
    throw new Error(`An error occurred while trying to retrieve MediaItems: ${error}`);
  });
}

/**
 * See net/disy/cadenza/workbook/web/selfservice/dto/ImportDataViewWithObjecttypeDto.java
 */
export interface ImportDataViewWithObjecttypeDto {
  dataViewNames: string[];
  events: ImportObjecttypeEventDto[];
  isDataCompatible: boolean;
  objecttypeName: string;
  objecttypePrintName: string;
  repositoryName: string;
}

export function getCompatibleObjectTypesForSelfService(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  dataType: string,
  srs: string,
) {
  const urlParams = new URLSearchParams({ dataType, srs });
  if (viewId) {
    urlParams.append('usedInView', viewId);
  }
  return ajax<ImportDataViewWithObjecttypeDto>(createWorkbookUrl(workbookNameAndHash) + `/objecttypes?${urlParams}`, {
    type: 'GET',
    headers: { Accept: MIMETYPE_JSON },
  });
}

export interface ConditionTableOptions {
  /** index to start loading rows from */
  offset?: number;
  /** number of rows to load */
  limit?: number;
  filterType?: string;
  /** text for filtering results that contain that text */
  filterText?: string;
  /** comma-separated list of columns to filter by */
  filterByColumn?: string;
  /** enables execution of the condition table query in context insensitive mode */
  forceContextInsensitiveMode?: boolean;
  orderAsc?: boolean;
  /** index of the column to order the rows by */
  orderByColumn?: number;
  noCount?: boolean;
  /** abort controller signal so the requests can be cancelled */
  signal?: AbortSignal;
}

/**
 * Returns condition table.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param worksheetId - Id of the active worksheet
 * @param conditionId - Id of the condition
 * @param parameters - Request parameters
 * @return Data of the table
 */
export function getWorkbookConditionTable(
  workbookNameAndHash: WorkbookNameAndHash,
  worksheetId: WorksheetId,
  conditionId: ConditionId,
  parameters: ConditionTableOptions,
) {
  const {
    offset = 0,
    limit = -1,
    filterText = '',
    /** no default sorting by first column - default sorting will be calculated in the backend from the attribute settings */
    orderByColumn = -1,
    signal = undefined,
    filterType = 'SUBSTRING',
    filterByColumn = '',
    orderAsc,
    forceContextInsensitiveMode = false,
    noCount = false,
  } = parameters;
  const baseUrl = createWorkbookUrl(workbookNameAndHash) + `/conditions/${conditionId}/table?`;
  let url =
    baseUrl +
    new URLSearchParams({
      activeInWorksheet: worksheetId,
      offset: String(offset),
      limit: String(limit),
      filterText,
      filterType,
      filterByColumn,
      orderByColumn: String(orderByColumn),
      forceContextInsensitiveMode: String(forceContextInsensitiveMode),
      noCount: String(noCount),
    });
  if (orderAsc !== undefined) {
    url = `${url}&orderAsc=${orderAsc}`;
  }
  return ajax<TableDataDto>(url, { dataType: 'json', signal });
}

/**
 * Returns values of spatial condition including geometries for each shape. (not the merged shape)
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param conditionId - Id of the condition
 * @return Condition values
 */
export function getSpatialConditionValues(workbookNameAndHash: WorkbookNameAndHash, conditionId: ConditionId) {
  const url = createWorkbookUrl(workbookNameAndHash) + `/conditions/${conditionId}/values`;
  return ajax<WorkbookSpatialConditionValueItemDto>(url, { dataType: 'json' });
}

/**
 * Returns values of spatial condition including geometries from the items of the current selection.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @return Condition values
 */
export function getSpatialConditionValueFromSelection(workbookNameAndHash: WorkbookNameAndHash) {
  return ajax<WorkbookSpatialConditionValueItemDto>(`${createWorkbookUrl(workbookNameAndHash)}/selection/geometries`, {
    method: 'GET',
    contentType: MIMETYPE_JSON,
  });
}

/**
 * Returns the object information from the items of the current selection.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param mapSrs - map srs
 * @param mapResolution - current resolution of the map view
 * @param signal - ?
 * @return The object info
 */
export function getObjectInfo(
  workbookNameAndHash: WorkbookNameAndHash,
  mapSrs: string,
  mapResolution: number,
  signal?: AbortSignal,
) {
  const searchParams = new URLSearchParams({
    [PARAMETER_SRS_CODE]: mapSrs ?? OL_DEFAULT_MAP_SRS,
    [PARAMETER_MAP_RESOLUTION]: String(mapResolution),
  });
  return ajax<ObjectInfoElementsDto[]>(`${createWorkbookUrl(workbookNameAndHash)}/selection/objects?${searchParams}`, {
    method: 'GET',
    contentType: MIMETYPE_JSON,
    signal,
  });
}

/**
 * Returns all the attributes that can be automatically calculated and shown to user when displaying object info..
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param geometryAttribute - Id of the geometry attribute
 * @param hierarchyLevel - hierarchy level
 * @return Dto containing all the possible attributes shown in feature info.
 */
export function getAllObjectInfoAttributes(
  workbookNameAndHash: WorkbookNameAndHash,
  geometryAttribute?: ActiveAttributeId,
  hierarchyLevel?: string,
) {
  const searchParams = new URLSearchParams();
  if (geometryAttribute) {
    searchParams.set('geometryAttribute', geometryAttribute);
  }
  if (hierarchyLevel) {
    searchParams.set('hierarchyLevel', hierarchyLevel);
  }
  return ajax<ObjectInfoAutoResolvedAttributesDto>(
    `${createWorkbookUrl(workbookNameAndHash)}/object-info/attributes?${searchParams}`,
    {
      method: 'GET',
      contentType: MIMETYPE_JSON,
    },
  );
}

export function getWorkbookConditionContextSensitiveData(
  workbookNameAndHash: WorkbookNameAndHash,
  worksheetId: WorksheetId,
  conditionId: ConditionId,
) {
  return getWorkbookConditionTable(workbookNameAndHash, worksheetId, conditionId, {
    offset: 0,
    limit: -1,
    orderByColumn: -1,
  });
}

interface WorkbookUrlOptions {
  worksheetId?: WorksheetId;
  viewId?: ViewId;
  dataViewId?: DataViewId;
  searchParams?: Record<string, string | boolean>;
}

/** Creates a workbook URL of the form `/repositories/{repositoryName}/workbooks/{workbookName}?workbookHash={workbookHash}` */
export function createWorkbookUrl(
  repositoryName: RepositoryName,
  workbookNameAndHash: Partial<WorkbookNameAndHash>,
  options?: WorkbookUrlOptions,
): string;
/** Creates a workbook URL of the form `/workbooks/{workbookName},hash={workbookHash}` */
export function createWorkbookUrl(
  workbookNameAndHash: Partial<WorkbookNameAndHash>,
  options?: WorkbookUrlOptions,
): string;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createWorkbookUrl(...args: any[]) {
  let repositoryName = '';
  let workbookNameAndHash: Partial<WorkbookNameAndHash>;
  let options: WorkbookUrlOptions | undefined;
  if (typeof args[0] === 'string') {
    [repositoryName, workbookNameAndHash, options] = args;
  } else {
    [workbookNameAndHash, options] = args;
  }
  const { worksheetId, viewId, dataViewId, searchParams } = options ?? {};
  assert(
    Number(worksheetId != null) + Number(viewId != null) + Number(dataViewId != null) <= 1,
    'Only one of worksheetId, viewId and dataViewId can be given at a time',
  );
  let url: string;
  if (repositoryName) {
    url = `/repositories/${repositoryName}/workbooks/${workbookNameAndHash.id}`;
  } else {
    const { id, hash } = workbookNameAndHash;
    assertNonNullable(id);
    assertNonNullable(hash);
    url = `/workbooks/${workbookNameAndHashString({ id, hash })}`;
  }

  if (worksheetId) {
    url += `/worksheets/${worksheetId}`;
  } else if (viewId) {
    url += `/views/${viewId}`;
  } else if (dataViewId) {
    url += `/dataViews/${dataViewId}`;
  }
  const params = new URLSearchParams(searchParams as Record<string, string> | undefined);
  if (repositoryName && workbookNameAndHash.hash) {
    params.set('workbookHash', workbookNameAndHash.hash);
  }
  // URLSearchParams.prototype.size is not supported in jsdom, despite the release notes saying different.
  // https://github.com/jsdom/whatwg-url/releases/tag/v13.0.0
  if ([...params].length) {
    url += '?' + params;
  }
  return url;
}

const workbookUrlPathRegexp = new RegExp(
  '/repositories/(?:[^,]*,)?(?<repositoryName>[\\w-]+)/workbooks/(?:[^,]*,)?(?<workbookName>[\\w-]+)(/worksheets/(?:[^,]*,)?(?<worksheetId>[\\w-]+)|/views/(?<viewId>[\\w-]+)|/dataViews/(?<dataViewId>[\\w-]+))?',
);

export function isWorkbookPage() {
  return parseWorkbookUrl(getLocation().href) != null;
}

/** Returns the object that contains parsed information from the workbook url. */
export function parseWorkbookUrl(url: string) {
  const match = decodeURI(url).match(workbookUrlPathRegexp);
  if (match) {
    const { repositoryName, workbookName, worksheetId, viewId, dataViewId } = match.groups as {
      repositoryName: RepositoryName;
      workbookName: WorkbookName;
      worksheetId?: WorksheetId;
      viewId?: ViewId;
      dataViewId?: DataViewId;
    };
    const workbookHash = (new URL(url).searchParams.get('workbookHash') ?? undefined) as WorkbookHash | undefined;
    const workbookNameAndHash: WorkbookNameAndHash | undefined = workbookHash && {
      id: workbookName,
      hash: workbookHash,
    };
    return {
      repositoryName,
      workbookName,
      workbookHash,
      workbookNameAndHash,
      worksheetId,
      viewId,
      dataViewId,
    };
  }
}

/** see net/disy/cadenza/workbook/web/dto/SaveWorkbookDto.java */
export interface SaveWorkbookDto {
  repositoryName?: RepositoryName;
  version?: WorkbookVersion;
  repositoryItemName?: WorkbookName;
  /* workbook print name */
  printName: string;
  folderItemName: string;
  labels: string[];
  displayState: DisplayState;
  comment: string;
  projectName?: ProjectName;
  dataProtectionContextId?: DataProtectionContextId;
  deletionDeadlineInfo?: DeletionDeadlineInfoDto;
  workbookNameAndHash: WorkbookNameAndHash;
}

/** see net/disy/cadenza/workbook/web/dto/WorkbookDisplayStateDto.java */
export interface DisplayState {
  isAnalysisContextDisplayed: boolean;
  isDataBrowserDisplayed: boolean;
  isDesignerDisplayed: boolean;
}

/** Fetches the details of a workbook for the management center */
export function getWorkbookDetail(workbookId: WorkbookId) {
  return ajax<WorkbookWithVersionDto>(`/repositories/${workbookId.repositoryName}/workbooks/${workbookId.itemName}`);
}

export function saveWorkbook(repositoryName: RepositoryName, options: SaveWorkbookDto) {
  return ajax<WorkOrderId>(`/repositories/${repositoryName}/create-workbook-work-orders`, {
    type: 'POST',
    data: JSON.stringify(options),
    contentType: MIMETYPE_JSON,
  });
}

export function retrieveWorkbookMetadata(uploadedFile: UploadedFileDto) {
  return ajax<WorkbookMetadataDto>('/workbooks/import/metadata', {
    type: 'POST',
    data: JSON.stringify(uploadedFile),
    contentType: MIMETYPE_JSON,
  });
}

export function saveWorkbookVersion(
  repositoryName: RepositoryName,
  workbookNameAndHash: WorkbookNameAndHash,
  { comment, displayState }: { comment?: string; displayState: DisplayState },
) {
  const data = JSON.stringify({ workbookNameAndHash, comment, displayState });
  return ajax<SaveWorkbookResult>(`/repositories/${repositoryName}/workbooks/${workbookNameAndHash.id}/versions`, {
    type: 'POST',
    data,
    contentType: MIMETYPE_JSON,
  });
}

export const updateVersionComment = (
  workbookName: WorkbookName,
  repositoryName: RepositoryName,
  version: number,
  comment: string,
) => {
  return ajax<void>(`/repositories/${repositoryName}/workbooks/${workbookName}/versions/${version}/comment`, {
    type: 'PUT',
    data: comment,
  });
};

export const deleteWorkbookVersions = (
  workbookName: WorkbookName,
  repositoryName: RepositoryName,
  versions: number[],
) => {
  return ajax<void>(`/repositories/${repositoryName}/workbooks/${workbookName}/versions`, {
    type: 'DELETE',
    contentType: MIMETYPE_JSON,
    data: JSON.stringify(versions),
  });
};

export const publishWorkbookVersion = (workbookName: WorkbookName, repositoryName: RepositoryName, version: number) => {
  return setWorkbookVersionPublished(repositoryName, workbookName, version, true);
};

export const unpublishWorkbookVersion = (
  workbookName: WorkbookName,
  repositoryName: RepositoryName,
  version: number,
) => {
  return setWorkbookVersionPublished(repositoryName, workbookName, version, false);
};

function setWorkbookVersionPublished(
  repositoryName: RepositoryName,
  workbookName: WorkbookName,
  version: number,
  published: boolean,
) {
  return ajax<void>(`/repositories/${repositoryName}/workbooks/${workbookName}/versions/${version}/published`, {
    type: 'PUT',
    data: Boolean(published).toString(),
    contentType: MIMETYPE_JSON,
  });
}

export const isWorkbookPublishable = (workbookName: WorkbookName, repositoryName: RepositoryName, version: number) => {
  return ajax<boolean>(`/repositories/${repositoryName}/workbooks/${workbookName}/versions/${version}/publishable`);
};

export const renameWorkbook = (workbookId: WorkbookId, newName: string) => {
  return ajax<WorkbookNameAndHash>(`/repositories/${workbookId.repositoryName}/workbooks/${workbookId.itemName}/name`, {
    type: 'PUT',
    data: newName,
    contentType: MIMETYPE_TEXT,
  });
};

/**
 * Download the given workbook as a ZIP file.
 */
export const downloadWorkbook = (workbookIdOrWorkbookNameAndHash: WorkbookId | WorkbookNameAndHash) => {
  const url =
    'repositoryName' in workbookIdOrWorkbookNameAndHash ?
      `/repositories/${workbookIdOrWorkbookNameAndHash.repositoryName}/workbooks/${workbookIdOrWorkbookNameAndHash.itemName}`
    : `/workbooks/${workbookNameAndHashString(workbookIdOrWorkbookNameAndHash)}`;
  return download(cadenzaUrl(url), {
    headers: {
      accept: MIMETYPE_ZIP,
    },
  });
};

export function importWorkbookFromFile(repositoryName: RepositoryName, importWorkbookDto: ImportWorkbookDto) {
  return ajax<WorkOrderId>(`/repositories/${repositoryName}/workbooks/import-workbook-work-orders`, {
    type: 'POST',
    data: JSON.stringify(importWorkbookDto),
    contentType: MIMETYPE_JSON,
  });
}

export const updateWorkbookFromFile = (
  { repositoryName, itemName }: WorkbookId,
  name: string,
  { fileName, signature }: { fileName: string; signature: string },
  dataProtectionContextId?: DataProtectionContextId,
) => {
  const formData = new FormData();
  formData.append('fileName', fileName);
  formData.append('signature', signature);
  formData.append('name', name);
  if (dataProtectionContextId) {
    formData.append('dataProtectionContextName', dataProtectionContextId.itemName);
  }
  return ajax<WorkOrderId>(`/repositories/${repositoryName}/workbooks/${itemName}/update-workbook-work-orders`, {
    type: 'PUT',
    cache: false,
    contentType: false,
    processData: false,
    data: formData,
    headers: {
      Accept: MIMETYPE_JSON,
    },
  });
};

export function getCompatibleObjecttypes(
  workbookNameAndHash: WorkbookNameAndHash,
  usedInView: ViewId,
  dataType: string,
  srs: string,
) {
  const baseUrl = createWorkbookUrl(workbookNameAndHash) + '/objecttypes';
  const params = new URLSearchParams({ usedInView, dataType, srs });
  const url = `${baseUrl}?${params.toString()}`;
  return ajax(url, {});
}

/**
 * Get a single LOB value for the current selection of the specified workbook.
 * The selection should be exactly one LOB attribute, otherwise it will result in an error.
 *
 * @param workbookNameAndHash - The workbook
 * @return The value of the selected LOB attribute, as a string
 */
export function getSelectedLobValue(workbookNameAndHash: WorkbookNameAndHash) {
  const url = createWorkbookUrl(workbookNameAndHash) + '/selection/lob-value';
  return ajax<string>(url, { dataType: 'text' });
}

interface WorkbookLegendStructureOptions {
  workbookNameAndHash: WorkbookNameAndHash;
  layerId: string;
  viewId?: ViewId;
  mapSize: DimensionDto;
  extent: WorldBoxDto;
  srsCode: string;
}

export function getLayerLegendStructure(
  { workbookNameAndHash, viewId, layerId, mapSize, extent, srsCode }: WorkbookLegendStructureOptions,
  signal?: AbortSignal,
) {
  const legendUrl = createWorkbookUrl(workbookNameAndHash, { viewId }) + `/layers/${layerId}/legend`;
  return ajax<LegendItemDto<DefaultLegendItemEntryDto | RouteLegendItemEntryDto | IsochroneLegendItemEntryDto>[]>(
    legendUrl,
    {
      signal,
      headers: { Accept: MIMETYPE_JSON },
      data: {
        WIDTH: mapSize[0],
        HEIGHT: mapSize[1],
        BBOX: extent.join(),
        SRS: srsCode,
      },
    },
  );
}

export function getWorkbookSubscriptionConfig() {
  return ajax<WorkbookJobScheduleConfiguration>('/job-schedule-config');
}

export function releaseWorkbookObjecttype(objecttypeId: ObjecttypeIdString) {
  return ajax(`/objecttypes/${window.btoa(objecttypeId)}/parent-workbook`, { type: 'DELETE' });
}

export function getWorkbookActiveAttributeValues(
  workbookNameAndHash: WorkbookNameAndHash,
  activeAttributeId: ActiveAttributeId,
  filterText = '',
) {
  const baseUrl = createWorkbookUrl(workbookNameAndHash) + `/active-attributes/${btoa(activeAttributeId)}/table?`;
  const url = baseUrl + new URLSearchParams({ filterText });
  return ajax<TableData<AttributeValueRowDto>>(url, { dataType: 'json' });
}

export function getAddAttributeToViewPredictedResult(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  attributeId: ActiveAttributeId | AttributeId,
) {
  const baseUrl = createWorkbookUrl(workbookNameAndHash, { viewId }) + `/add-attribute-predicted-result?`;
  const url = baseUrl + new URLSearchParams({ attributeId });
  return ajax<AddAttributeToViewPredictedResult>(url, { dataType: 'json' });
}
